import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Form, Row, Col } from "react-bootstrap";
import ReactSelect, { createFilter } from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import * as PersonFormFields from "../../../common/person/personformfields";
import { kevaMapper, selectMapper } from "../../../common/utils";
import { asyncSelectSearchSettings, obsoleteOptionStyle, reactSelectSearchSettings } from "../../../common/reactselect";
import { authSelector } from "../../../reducers";
import DefaultDatePicker from "../../../common/datepicker/DefaultDatePicker";


export function ContractGroup2({ state, dispatch, formErrors, onChange }) {
  const disabled = state.status === "COMPLETE";
  const { t, i18n } = useTranslation();
  const auth = useSelector(authSelector);
  const occupationTypes = auth.company?.occupationTypes ?? "MULTI";
  const [cbaCodes, setCbaCodes] = useState([]);
  const [tk10, setTk10] = useState([]);
  const [keva, setKeva] = useState([]);
  const prefix = "contractDetails";
  const details = state.details ?? {};
  const hasPrimaryDates = Boolean(details.startDate || details.endDate);
  const effectiveStartDate = hasPrimaryDates ? (details.startDate ?? "") : (details.externalStartDate ?? "");
  const effectiveEndDate = hasPrimaryDates ? (details.endDate ?? "") : (details.externalEndDate ?? "");
  const showEndDate = details.fixedDuration || Boolean(effectiveEndDate);

  useEffect(() => {
    async function fetchNamespaces() {
      await i18n.loadNamespaces(["cbacodes", "tk10", "keva"]);

      setCbaCodes(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, "cbacodes") ?? {})
        .map(([key, value]) => {
          const isObsolete = value.startsWith("* ");
          return {
            value: key,
            label: `${key} - ${isObsolete ? value.slice(2) : value}`,
            isObsolete,
          }
        }));

      setTk10(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, "tk10") ?? {})
        .map(selectMapper));

      // includes trafi codes (keva lists are only in fi,sv)
      // https://www.botsplash.com/post/optimize-your-react-select-component-to-smoothly-render-10k-data
      setKeva(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, "keva") ?? {})
        .map(kevaMapper)
      );

    }
    fetchNamespaces();
  }, [i18n.resolvedLanguage]);

  const selectChange = (option, element) => {
    const payloadValue = option ? option.value : null;
    dispatch({ type: element.name, payload: payloadValue });
  };

  const CustomOption = ({ children, ...props }) => {
    // eslint-disable-next-line no-unused-vars
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = { ...props, innerProps: rest };
    return (
      <components.Option {...newProps}>
        {children}
      </components.Option>
    );
  };

  CustomOption.propTypes = {
    innerProps: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired
  };

  const loadOptions = (inputValue, callBack) => {
    const results = keva.filter(item =>
      item.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    if (results.length <= 1000) callBack(results);
  };

  const handleChange = ({ target: { id, value } }) => dispatch({ type: id, payload: value });

  const customReactSelectSearchSettings = reactSelectSearchSettings(t);
  customReactSelectSearchSettings.styles = {
    ...customReactSelectSearchSettings.styles,
    option: obsoleteOptionStyle, // gray out obsolete options in react-select dropdowns
    singleValue: obsoleteOptionStyle, // also gray out the selected value if it's obsolete
  };

  return (
    <>
      <Row>
        <Form.Group as={Col} xl={4} className="position-relative mt-3" controlId="personStartDate">
          <PersonFormFields.StartDate
            onChange={(iso) => dispatch({ type: "personStartDate", payload: iso ?? "" })}
            value={state.person?.startDate ?? ""}
            formErrors={formErrors}
            disabled={disabled}
          />
        </Form.Group>
        <Form.Group as={Col} xl={4} className="position-relative mt-3" controlId="startDate">
          <DefaultDatePicker
            id={`${prefix}-startDate`}
            name="startDate"
            label={t("NewContractStartDate", "Uuden työsopimuksen alkupvm.")}
            value={effectiveStartDate}
            onChange={(iso) => dispatch({ type: "startDate", payload: iso ?? "" })}
            disabled={disabled}
          />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={8} className="position-relative mt-3" controlId="fixedDuration">
          <Form.Label />
          <Form.Check inline type="radio" disabled={disabled} label={t("UntilFurtherNotice", "Toistaiseksi voimassaoleva")} id="fixedDuration.false" checked={!state.details.fixedDuration} onChange={() => dispatch({ type: "fixedDuration.false" })} />
          <Form.Check inline type="radio" disabled={disabled} label={t("FixedDuration", "Määräaikainen")} id="fixedDuration.true" checked={state.details.fixedDuration} onChange={() => dispatch({ type: "fixedDuration.true" })} />
        </Form.Group>
      </Row>

      <Row>
        {showEndDate &&
          <Form.Group as={Col} xl={4} className="position-relative mt-4" controlId="endDate">
            <DefaultDatePicker
              id={`${prefix}-endDate`}
              name="endDate"
              label={t("EndofContract", "Päättymisajankohta")}
              value={effectiveEndDate}
              onChange={(iso) => dispatch({ type: "endDate", payload: iso ?? "" })}
              disabled={disabled}
            />
          </Form.Group>
        }
        {state.details.fixedDuration &&
          <Form.Group as={Col} xl={8} className="position-relative" controlId="fixedDurationExplanation">
            <Form.Label>{t("FixedDurationExplanation", "Määräaikaisuuden peruste")}</Form.Label>
            <Form.Control disabled={disabled} value={state.details.fixedDurationExplanation ?? ""} onChange={handleChange} />
          </Form.Group>
        }
        {!state.details.fixedDuration &&
          <Form.Group as={Col} xl={8} className="position-relative" controlId="fixedDurationExplanationPlaceholder">
            <Form.Label></Form.Label>
          </Form.Group>
        }
      </Row>

      <Row>
        <Form.Group as={Col} xl={2} className="position-relative" controlId="trialPeriod">
          <Form.Label></Form.Label>
          <Form.Check type="checkbox" disabled={disabled} label={t("TrialPeriod", "Sovelletaan koeaikaa")} id="trialPeriod.toggle" checked={state.details.trialPeriod} onChange={() => dispatch({ type: "trialPeriod.toggle" })} />
          <Form.Label></Form.Label>
        </Form.Group>
        {state.details.trialPeriod &&
          <Form.Group as={Col} xl={2} className="position-relative" controlId="TrialPeriodLengthDays">
            <Form.Label></Form.Label>
            <Form.Check type="checkbox" disabled={disabled} label={t("InDays", "Päivinä")} id="TrialPeriodLengthDays.toggle" checked={state.details.trialPeriodLengthDays} onChange={() => dispatch({ type: "trialPeriodLengthDays.toggle" })} />
            <Form.Label></Form.Label>
          </Form.Group>
        }
        {state.details.trialPeriod && <>
          <Form.Group as={Col} xl={4} className="position-relative" controlId="trialPeriodLength">
            <Form.Label>{state.details.trialPeriodLengthDays ? t("TrialPeriodLengthDays", "Koeajan pituus päivinä") : t("TrialPeriodLength", "Koeajan pituus kuukausina")}</Form.Label>
            <Form.Control type="number" min={1} step={1} disabled={disabled} value={state.details.trialPeriodLength ?? ""} onChange={evt => {
              const value = evt.target.valueAsNumber;
              dispatch({ type: "trialPeriodLength", payload: isNaN(value) ? null : value });
            }} />
          </Form.Group>
        </>}
        {state.details.trialPeriod && <>
          <Form.Group as={Col} xl={4} className="position-relative mt-4" controlId="trialPeriodEndDate">
            <DefaultDatePicker
              id={`${prefix}-trialPeriodEndDate`}
              name="trialPeriodEndDate"
              label={t("TrialPeriodEndDate", "Koeaika päättyy")}
              value={state.details.trialPeriodEndDate ?? ""}
              onChange={(iso) => dispatch({ type: "trialPeriodEndDate", payload: iso ?? "" })}
              disabled={disabled}
            />
          </Form.Group>
        </>}
      </Row>
      {state.details.trialPeriod && <>
        <Row>
          <Form.Group as={Col} xl={12} className="position-relative" controlId="trialPeriodExplanation">
            <Form.Label>{t("TrialPeriodExplanation", "Koeaika selite")}</Form.Label>
            <Form.Control as="textarea" rows={3} disabled={disabled} value={state.details.trialPeriodExplanation ?? ""} onChange={handleChange} />
          </Form.Group>
        </Row>
      </>}
      <Row>
        <Form.Group as={Col} xl={12} className="position-relative" controlId="termOfNotice">
          <Form.Label>{t("TermOfNotice", "Irtisanomisaika")}</Form.Label>
          <Form.Control as="textarea" rows={3} disabled={disabled} value={state.details.termOfNotice ?? ""} onChange={handleChange} />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={12} className="position-relative" controlId="cbaCode">
          <Form.Label>{t("CbaCode", "Sovellettava työehtosopimus")}</Form.Label>
          <ReactSelect
            name="cbaCode"
            inputId="cbaCode"
            isDisabled={disabled}
            options={cbaCodes}
            isClearable
            value={cbaCodes.find(option => option.value === state.details.cbaCode) || null}
            onChange={selectChange}
            {...customReactSelectSearchSettings}
          />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={12} className="position-relative" controlId="place">
          <Form.Label>{t("Place", "Työntekopaikka/alue")}</Form.Label>
          <Form.Control as="textarea" rows={3} disabled={disabled} value={state.details.place ?? ""} onChange={handleChange} />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={6} className="position-relative" controlId="jobTitle">
          <Form.Label>{t("JobTitle", "Tehtävänimike")}</Form.Label>
          <Form.Control disabled={disabled} value={state.details.jobTitle ?? ""} onChange={handleChange} />
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={4} className="position-relative" controlId="occupationCodeTK10">
          {(occupationTypes === "MULTI" || occupationTypes === "TK10") && <>
            <Form.Label>{t("OccupationCodeTK10", "Ammattinimike (TK10)")}</Form.Label>
            <ReactSelect
              name="occupationCodeTK10"
              inputId="occupationCodeTK10"
              filterOption={createFilter({ ignoreAccents: false })}
              isDisabled={disabled}
              options={tk10}
              isClearable
              value={tk10.find(option => option.value === state.details.occupationCodeTK10) || null}
              onChange={selectChange}
              {...reactSelectSearchSettings(t)}
            />
          </>}
        </Form.Group>
        <Form.Group as={Col} xl={4} className="position-relative" controlId="occupationCodeKeva">
          {(occupationTypes === "MULTI" || occupationTypes === "KEVA" || occupationTypes === "TRAFI") && <>
            <Form.Label>{t("OccupationCodeKeva", "Ammattinimike (KEVA)")}</Form.Label>
            <AsyncSelect
              loadOptions={loadOptions}
              name="occupationCodeKeva"
              inputId="occupationCodeKeva"
              isDisabled={disabled}
              isClearable
              value={keva.find(option => option.value === state.details.occupationCodeKeva) || null}
              onChange={selectChange}
              {...asyncSelectSearchSettings(t)}
            />
          </>}
        </Form.Group>
      </Row>

      <Row>
        <Form.Group as={Col} xl={12} className="position-relative" controlId="assignment">
          <Form.Label>{t("JobDescription", "Työtehtävät")}</Form.Label>
          <Form.Control as="textarea" rows={3} disabled={disabled} value={state.details.assignment ?? ""} onChange={handleChange} />
        </Form.Group>
      </Row>

    </>
  );

}
